<script setup lang="ts">
/**
 * Side panel card "AI กำลังดูแล" (docs/ai-mode.md): read-only view of what the AI understood so far (session.state,
 * owned by the sales brain) and the tools of its last turn. Calm and foldable; renders whatever keys exist.
 */
import { computed, watch } from 'vue'
import { useAiMode } from '../../stores/aimode'
import { useInbox } from '../../stores/inbox'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ convId: string }>()
const inbox = useInbox()
const aimode = useAiMode()

const conv = computed(() => inbox.convs[props.convId] ?? null)
const view = computed(() => aimode.views[props.convId] ?? null)
// folded every time a chat opens (owner 28 ก.ย.: "ย่อไว้ก่อนเสมอ ถ้าอยากดูเดี๋ยวกดดูเอง")
watch(() => props.convId, () => (aimode.cardOpen = false))
const session = computed(() => (conv.value?.ai_mode && view.value?.session?.open ? view.value.session : null))
const state = computed<Record<string, unknown>>(() => session.value?.state ?? {})
const lastTurn = computed(() => view.value?.recent_turns[0] ?? null)

const STAGE: Record<string, string> = {
  greeting: 'ทักทาย',
  discover: 'ถามความต้องการ',
  choose_package: 'เลือกแพ็ก',
  choose_menu: 'เลือกเมนู',
  menu: 'เลือกเมนู',
  collect_address: 'ขอชื่อและที่อยู่',
  address: 'ขอชื่อและที่อยู่',
  quote: 'แจ้งยอด',
  payment: 'รอชำระเงิน',
  slip: 'ตรวจสลิป',
  order: 'ลงคำสั่งซื้อ',
  done: 'เสร็จแล้ว',
}
const KNOWN = new Set(['stage', 'package', 'items', 'menu_code', 'customer', 'address', 'quote', 'slip'])

function text(v: unknown): string {
  if (v === null || v === undefined || v === '') return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  if (Array.isArray(v)) return v.map(text).filter(Boolean).join(', ')
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>
    for (const k of ['label', 'name', 'title', 'text']) if (typeof o[k] === 'string' && o[k]) return o[k] as string
    return Object.entries(o)
      .map(([k, x]) => [k, text(x)] as const)
      .filter(([, x]) => x)
      .map(([k, x]) => `${k} ${x}`)
      .join(' · ')
  }
  return ''
}
const baht = (n: number): string => `${n.toLocaleString('th-TH', { maximumFractionDigits: 2 })} บาท`

const rows = computed<{ k: string; v: string }[]>(() => {
  const s = state.value
  const out: { k: string; v: string }[] = []
  if (s.package) out.push({ k: 'แพ็กเกจ', v: text(s.package) })
  if (Array.isArray(s.items) && s.items.length) {
    const qty = s.items.reduce((n: number, it) => n + (typeof (it as { qty?: unknown }).qty === 'number' ? ((it as { qty: number }).qty) : 1), 0)
    out.push({ k: 'เมนู', v: `${s.items.length} รายการ · ${qty} มื้อ${s.menu_code ? ` · รหัส ${text(s.menu_code)}` : ''}` })
  } else if (s.menu_code) out.push({ k: 'เมนู', v: `รหัส ${text(s.menu_code)}` })
  if (s.address) {
    const a = s.address as Record<string, unknown>
    const parts = typeof s.address === 'object' ? [text(a.area ?? a.zone ?? a.district), a.zip ? `รหัส ${text(a.zip)}` : ''] : [text(s.address)]
    const v = parts.filter(Boolean).join(' · ') || text(s.address)
    if (v) out.push({ k: 'พื้นที่', v })
  }
  if (s.customer) out.push({ k: 'ลูกค้า', v: text(s.customer) })
  if (s.quote) {
    const q = s.quote as Record<string, unknown>
    const total = typeof q === 'object' && typeof q.total === 'number' ? baht(q.total) : text(s.quote)
    out.push({ k: 'ยอดที่แจ้ง', v: total })
  }
  if (s.slip) {
    const sl = s.slip as Record<string, unknown>
    out.push({ k: 'สลิป', v: typeof sl === 'object' && typeof sl.label === 'string' ? sl.label : text(s.slip) })
  }
  for (const [k, v] of Object.entries(s)) {
    if (KNOWN.has(k)) continue
    const t = text(v)
    if (t) out.push({ k, v: t })
  }
  return out
})
const stage = computed(() => {
  const st = state.value.stage
  return typeof st === 'string' && st ? (STAGE[st] ?? st) : null
})
const tools = computed(() =>
  (lastTurn.value?.tools ?? []).map((t) => {
    const o = (t ?? {}) as Record<string, unknown>
    const name = typeof o.name === 'string' ? o.name : typeof o.tool === 'string' ? o.tool : text(t)
    return { name, bad: o.ok === false || !!o.error }
  }),
)
const understood = computed(() => {
  const d = lastTurn.value?.debug ?? null
  const u = d && (d.understood ?? d.summary ?? d.note)
  return typeof u === 'string' ? u : ''
})
</script>

<template>
  <section v-if="session" class="aic" aria-label="AI กำลังดูแล">
    <button type="button" class="aic-h" :aria-expanded="aimode.cardOpen" @click="aimode.cardOpen = !aimode.cardOpen">
      <span class="aic-live" aria-hidden="true"></span>
      <span class="aic-t">AI กำลังดูแล</span>
      <span v-if="stage" class="aic-stage">{{ stage }}</span>
      <span class="grow"></span>
      <span class="aic-n">{{ session.turns }} รอบ</span>
      <Icon :name="aimode.cardOpen ? 'chevronUp' : 'chevronDown'" />
    </button>
    <div v-if="aimode.cardOpen" class="aic-b">
      <p v-if="!rows.length" class="aic-empty">ยังไม่มีข้อมูลการขาย AI จะเติมเมื่อคุยกับลูกค้า</p>
      <dl v-else class="aic-dl">
        <template v-for="r in rows" :key="r.k">
          <dt>{{ r.k }}</dt>
          <dd>{{ r.v }}</dd>
        </template>
      </dl>
      <p v-if="understood" class="aic-u"><Icon name="sparkle" />{{ understood }}</p>
      <div v-if="tools.length" class="aic-tools">
        <span class="aic-tl">รอบล่าสุดใช้</span>
        <span v-for="(t, i) in tools" :key="i" class="aic-tool" :class="{ bad: t.bad }">{{ t.name }}</span>
      </div>
      <p class="aic-foot">ดูอย่างเดียว · พิมพ์ส่งเมื่อไร AI หยุดทันที</p>
    </div>
  </section>
</template>

<style scoped>
.aic {
  flex: none;
  margin: 12px 16px 0;
  border: 1px solid #cfe6da;
  border-radius: 12px;
  background: #f7fbf9;
  overflow: hidden;
}
.aic-h {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 40px;
  padding: 8px 12px;
  font-size: 13px;
  line-height: 20px;
}
.aic-h :deep(.ic) {
  width: 15px;
  height: 15px;
  color: var(--mut);
}
.aic-live {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ac);
  box-shadow: 0 0 0 3px rgba(14, 122, 92, 0.16);
}
.aic-t {
  font-weight: 600;
  color: var(--ac2);
}
.aic-stage {
  height: 22px;
  padding: 0 8px;
  border-radius: 6px;
  background: #e3f1ea;
  color: #0a5e47;
  font-size: 12px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;
}
.aic-n {
  font-size: 12px;
  color: var(--mut);
  white-space: nowrap;
}
.aic-b {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 12px 12px;
  border-top: 1px solid #e0efe7;
  padding-top: 10px;
}
.aic-dl {
  display: grid;
  grid-template-columns: 84px 1fr;
  column-gap: 12px;
  row-gap: 4px;
  margin: 0;
  font-size: 13px;
  line-height: 20px;
}
.aic-dl dt {
  color: var(--mut);
}
.aic-dl dd {
  margin: 0;
  min-width: 0;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.aic-empty,
.aic-foot {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.aic-u {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e0efe7;
  font-size: 12.5px;
  line-height: 19px;
  color: #24453a;
}
.aic-u :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: 2px;
  color: var(--ac);
}
.aic-tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.aic-tl {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.aic-tool {
  height: 22px;
  padding: 0 8px;
  border-radius: 6px;
  background: #eef2f0;
  color: #3e4d46;
  font-family: var(--mono);
  font-size: 12px;
  line-height: 22px;
}
.aic-tool.bad {
  background: var(--crits);
  color: #a61b12;
}
</style>
